import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import { Search24Regular, ArrowLeft24Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { useIsMobile } from '../hooks/useIsMobile';

interface SearchResult {
  id: string;
  type: 'customer' | 'order' | 'invoice' | 'quote' | 'contract' | 'product' | 'inventory';
  title: string;
  subtitle?: string;
  details?: string;
}

interface GroupedResults {
  customers: SearchResult[];
  orders: SearchResult[];
  products: SearchResult[];
  inventory: SearchResult[];
  invoices: SearchResult[];
  quotes: SearchResult[];
  contracts: SearchResult[];
}

const useStyles = makeStyles({
  searchWrapper: {
    position: 'relative',
  },
  searchInput: {
    width: '320px',
  },
  collapsedButton: {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      color: '#ffffff',
    },
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: '0',
    right: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    maxHeight: '500px',
    overflowY: 'auto',
    zIndex: 2000,
  },
  resultGroup: {
    ...shorthands.padding('8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  groupHeader: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    ...shorthands.padding('8px', '16px', '4px'),
  },
  resultItem: {
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  resultTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '4px',
    color: tokens.colorNeutralForeground1,
  },
  resultSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '2px',
  },
  resultDetails: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    ...shorthands.padding('24px', '16px'),
    textAlign: 'center',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  loadingState: {
    ...shorthands.padding('24px', '16px'),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  showMoreLink: {
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  // ── Mobile full-screen sheet ────────────────────────────────────────────
  sheet: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
  },
  sheetTopBar: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  sheetInput: {
    flex: 1,
    minWidth: 0,
  },
  sheetResults: {
    flex: 1,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },
  sheetHint: {
    ...shorthands.padding('24px', '16px'),
    textAlign: 'center',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
});

const GlobalSearch: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [results, setResults] = useState<GroupedResults>({
    customers: [],
    orders: [],
    products: [],
    inventory: [],
    invoices: [],
    quotes: [],
    contracts: [],
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const expand = () => {
    setExpanded(true);
    // Defer focus until the Input mounts.
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const collapse = () => {
    setExpanded(false);
    setSearchQuery('');
    setShowDropdown(false);
  };

  // Click outside: close the dropdown; collapse back to icon only if the
  // user hasn't typed anything (otherwise keep state so they can return to it).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        if (!searchQuery.trim()) setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchQuery]);

  // Cmd/Ctrl+K to open & focus; Escape to collapse.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!expanded) {
          expand();
        } else {
          inputRef.current?.focus();
        }
      } else if (e.key === 'Escape' && expanded) {
        collapse();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expanded]);

  // Fetch search results
  useEffect(() => {
    const fetchResults = async () => {
      if (searchQuery.trim().length < 2) {
        setResults({
          customers: [],
          orders: [],
          products: [],
          inventory: [],
          invoices: [],
          quotes: [],
          contracts: [],
        });
        setShowDropdown(false);
        return;
      }

      try {
        setLoading(true);
        setShowDropdown(true);
        const response = await apiFetch(`/global-search?q=${encodeURIComponent(searchQuery)}&limit=3`);

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();

        // Group results by type
        const grouped: GroupedResults = {
          customers: [],
          orders: [],
          products: [],
          inventory: [],
          invoices: [],
          quotes: [],
          contracts: [],
        };

        data.results.forEach((result: SearchResult) => {
          // Handle special case for inventory (doesn't pluralize with 's')
          if (result.type === 'inventory') {
            grouped.inventory.push(result);
          } else if (grouped[`${result.type}s` as keyof GroupedResults]) {
            grouped[`${result.type}s` as keyof GroupedResults].push(result);
          }
        });

        setResults(grouped);
      } catch (err) {
        console.error('Search error:', err);
        setResults({
          customers: [],
          orders: [],
          products: [],
          inventory: [],
          invoices: [],
          quotes: [],
          contracts: [],
        });
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleResultClick = (result: SearchResult) => {
    collapse();

    // Navigate based on result type
    switch (result.type) {
      case 'customer':
        navigate(`/customers/${result.id}`);
        break;
      case 'order':
        navigate(`/orders/${result.id}`);
        break;
      case 'product':
        navigate(`/inventory?product=${result.id}`);
        break;
      case 'inventory':
        navigate(`/inventory?item=${result.id}`);
        break;
      case 'invoice':
        navigate(`/invoices/${result.id}`);
        break;
      case 'quote':
        navigate(`/quotes/${result.id}`);
        break;
      case 'contract':
        navigate(`/contracts/${result.id}`);
        break;
    }
  };

  const getTotalResults = () => {
    return (
      results.customers.length +
      results.orders.length +
      results.products.length +
      results.inventory.length +
      results.invoices.length +
      results.quotes.length +
      results.contracts.length
    );
  };

  const getGroupLabel = (type: string) => {
    switch (type) {
      case 'customers':
        return 'Customers';
      case 'orders':
        return 'Orders';
      case 'products':
        return 'Products';
      case 'inventory':
        return 'Inventory';
      case 'invoices':
        return 'Invoices';
      case 'quotes':
        return 'Quotes';
      case 'contracts':
        return 'Contracts';
      default:
        return type;
    }
  };

  const handleShowMoreResults = () => {
    const q = searchQuery;
    collapse();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const shortcutHint = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform) ? '⌘K' : 'Ctrl+K';

  if (!expanded) {
    return (
      <div ref={wrapperRef} className={styles.searchWrapper}>
        <Tooltip content={`Search (${shortcutHint})`} relationship="label">
          <Button
            className={styles.collapsedButton}
            appearance="subtle"
            icon={<Search24Regular />}
            onClick={expand}
            aria-label={`Search (${shortcutHint})`}
          />
        </Tooltip>
      </div>
    );
  }

  const showResults = showDropdown && searchQuery.trim().length >= 2;

  const resultsBody = (
    <>
      {loading && (
        <div className={styles.loadingState}>
          <Spinner size="small" />
        </div>
      )}

      {!loading && getTotalResults() === 0 && (
        <div className={styles.emptyState}>No results found for "{searchQuery}"</div>
      )}

      {!loading && getTotalResults() > 0 && (
        <>
          {Object.entries(results).map(([type, items]) => {
            if (items.length === 0) return null;

            return (
              <div key={type} className={styles.resultGroup}>
                <div className={styles.groupHeader}>{getGroupLabel(type)}</div>
                {items.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    className={styles.resultItem}
                    onClick={() => handleResultClick(result)}
                  >
                    <div className={styles.resultTitle}>{result.title}</div>
                    {result.subtitle && (
                      <div className={styles.resultSubtitle}>{result.subtitle}</div>
                    )}
                    {result.details && (
                      <div className={styles.resultDetails}>{result.details}</div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          <div className={styles.showMoreLink} onClick={handleShowMoreResults}>
            Show more results
          </div>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div ref={wrapperRef} className={styles.sheet} role="dialog" aria-modal="true">
        <div className={styles.sheetTopBar}>
          <Button
            appearance="subtle"
            aria-label="Close search"
            icon={<ArrowLeft24Regular />}
            onClick={collapse}
          />
          <Input
            className={styles.sheetInput}
            contentBefore={<Search24Regular />}
            contentAfter={
              searchQuery ? (
                <Button
                  appearance="transparent"
                  size="small"
                  aria-label="Clear search"
                  icon={<Dismiss20Regular />}
                  onClick={() => setSearchQuery('')}
                />
              ) : undefined
            }
            placeholder="Search…"
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            input={{ ref: inputRef }}
          />
        </div>
        <div className={styles.sheetResults}>
          {showResults ? resultsBody : (
            <div className={styles.sheetHint}>
              Type at least 2 characters to search across customers, orders, products, and more.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.searchWrapper}>
      <Input
        className={styles.searchInput}
        contentBefore={<Search24Regular />}
        placeholder="Search..."
        value={searchQuery}
        onChange={(e, data) => setSearchQuery(data.value)}
        input={{ ref: inputRef }}
      />

      {showResults && (
        <div className={styles.dropdown}>{resultsBody}</div>
      )}
    </div>
  );
};

export default GlobalSearch;
